import React, { useEffect, useState } from "react";
import {
  VariantControl,
  VariantDot,
  VariantType,
} from "../../components/variant/VariantControl";
import { NewPropertyState } from "../../../usePropertyStateV2";
import { ComboBoxOptionType, DiscreteInput } from "../../components/input";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
  ALIGN_CONTENT_OPTIONS,
  FLEX_DIRECTION_OPTIONS,
  FLEX_WRAP_OPTIONS,
} from "../../components/variant/variantOptions";
import { PropertyType } from "@/tailwind/tailwindUtils";

// TODO: Split all fields out from the TailwindStylingPanelV2.tsx file like this

interface AlignChildrenProps {
  alignContentState: NewPropertyState;
  flexDirectionState: NewPropertyState;
  justifyContentState: NewPropertyState;
  alignItemsState: NewPropertyState;
  pickTailwindClassForProperty: (props: {
    option: ComboBoxOptionType;
    propertyType: PropertyType;
    setTemporaryDraggingValue: (value: string | null) => void;
    variant?: string | null;
  }) => void;
  clearTailwindClassForProperty: (props: {
    propertyType: PropertyType;
    setTemporaryDraggingValue: (value: string | null) => void;
    variant?: string | null;
  }) => void;
}

const AlignChildrenField: React.FC<AlignChildrenProps> = React.memo(
  ({
    alignContentState,
    flexDirectionState,
    justifyContentState,
    alignItemsState,
    pickTailwindClassForProperty,
    clearTailwindClassForProperty,
  }) => {
    return (
      <div className="flex flex-col items-start justify-between gap-1 relative">
        <div className="text-xs  text-text-secondary font-normal">
          Align children
        </div>
        <ContextMenu>
          <ContextMenuTrigger asChild>
            <div className="max-w-52 w-full relative">
              <VariantDot valueByVariant={alignContentState.valueByVariant} />
              <DiscreteInput
                options={ALIGN_CONTENT_OPTIONS}
                onOptionSelected={(discreteOption) => {
                  pickTailwindClassForProperty({
                    option: discreteOption.option,
                    propertyType: PropertyType.ALIGN_CONTENT,
                    setTemporaryDraggingValue:
                      alignContentState.setTemporaryDraggingValue,
                  });
                }}
                selected={alignContentState.propertyValue}
                set={alignContentState.isSet}
                onDeselect={() => {
                  clearTailwindClassForProperty({
                    propertyType: PropertyType.ALIGN_CONTENT,
                    setTemporaryDraggingValue:
                      alignContentState.setTemporaryDraggingValue,
                  });
                }}
              />
            </div>
          </ContextMenuTrigger>
          <ContextMenuContent className="p-0 border-none">
            <VariantControl
              valueByVariant={alignContentState.valueByVariant}
              onVariantChange={(variant: VariantType, value) => {
                const option = ALIGN_CONTENT_OPTIONS.find(
                  (opt) => opt.key === value,
                );
                if (option) {
                  pickTailwindClassForProperty({
                    option: option.option,
                    propertyType: PropertyType.ALIGN_CONTENT,
                    setTemporaryDraggingValue:
                      alignContentState.setTemporaryDraggingValue,
                    variant,
                  });
                }
              }}
              onClearVariant={(variant: VariantType) => {
                clearTailwindClassForProperty({
                  propertyType: PropertyType.ALIGN_CONTENT,
                  setTemporaryDraggingValue:
                    alignContentState.setTemporaryDraggingValue,
                  variant,
                });
              }}
              currentFlexDirection={flexDirectionState.propertyValue}
              currentJustifyContent={justifyContentState.propertyValue}
              currentAlignItems={alignItemsState.propertyValue}
              controlType="align-content"
            >
              {(variant, expanded, setExpanded) => {
                if (!expanded || !variant) return null;
                const variantValue =
                  alignContentState.valueByVariant[variant]?.value || "";
                return (
                  <DiscreteInput
                    options={ALIGN_CONTENT_OPTIONS}
                    onOptionSelected={(discreteOption) => {
                      pickTailwindClassForProperty({
                        option: discreteOption.option,
                        propertyType: PropertyType.ALIGN_CONTENT,
                        setTemporaryDraggingValue:
                          alignContentState.setTemporaryDraggingValue,
                        variant,
                      });
                    }}
                    selected={variantValue}
                    set={!!variantValue}
                    onDeselect={() => {
                      clearTailwindClassForProperty({
                        propertyType: PropertyType.ALIGN_CONTENT,
                        setTemporaryDraggingValue:
                          alignContentState.setTemporaryDraggingValue,
                        variant,
                      });
                    }}
                  />
                );
              }}
            </VariantControl>
          </ContextMenuContent>
        </ContextMenu>
      </div>
    );
  },
);

export default AlignChildrenField;
